import { MutatorState } from '@aesop-fables/scrinium';
import { Box, Link, Typography } from '@mui/material';
import _ from 'lodash';
import { LicenseAgreementData } from '../../api/apis/LicenseAgreementApi';
import { PricebookBundle, PaymentTypeEnum } from '../../models/PricebookData';
import { OrgSignUpStateData, State } from '../../models/SignUpData';
import { PackageCard } from './components';
import AccentBar from '../../dashboard/rev-share/components/AccentBar';
import useTermsOfServiceDocs from '../../hooks/useTermsOfServiceDocs';
import { useAddOnPricebookLookup } from '../../hooks/useAddOnPricebookLookup';
import { usePricebookOptions } from '../../hooks/usePricebookOptions';
import { useTheme } from '@mui/material/styles';

interface PackageOptionsProps {
  memberPaidOptions: PricebookBundle[];
  orgPaidOptions: PricebookBundle[];
  selectedPricebook?: PricebookBundle;
  setSelectedPricebook: React.Dispatch<React.SetStateAction<PricebookBundle | undefined>>;
  pricebookSelections?: PricebookBundle[];
  licenseAgreements?: LicenseAgreementData[];
  signUpState?: State;
  updateSignUpState: MutatorState<OrgSignUpStateData>;
  multiplePricebooks: boolean;
  addingPackages?: boolean;
}

const PackageOptions: React.FC<PackageOptionsProps> = ({
  memberPaidOptions,
  orgPaidOptions,
  selectedPricebook,
  setSelectedPricebook,
  pricebookSelections,
  licenseAgreements,
  signUpState,
  updateSignUpState,
  multiplePricebooks,
  addingPackages,
}) => {
  const theme = useTheme();
  const termsOfServiceDocs = useTermsOfServiceDocs();

  const getActiveAgreement = (bundle: PricebookBundle) =>
    licenseAgreements?.find(
      license => license.pricebookId === bundle.pricebook.id && license.docStatus,
    );

  const allOptions = [...memberPaidOptions, ...orgPaidOptions];
  const pricebookOptions = usePricebookOptions();
  const addOnPricebookLookup = useAddOnPricebookLookup(pricebookOptions);
  const hasActiveAgreement = allOptions.some(bundle => Boolean(getActiveAgreement(bundle)));
  const promotionalOfferExists = allOptions.some(pb => pb.pricebook.metadata.promotionalOffer);
  const paidNonProfitPricebookOrEduPricebookExists = allOptions.some(
    pb =>
      (pb.pricebook.metadata.nonProfit && (pb.prices[0]?.price ?? 0) > 0) ||
      pb.pricebook.metadata.subHeader,
  );
  const introPeriodExists = allOptions.some(bundle =>
    bundle.prices.some(price => price.introductionPeriodInDays > 1),
  );
  const meteredPayment = orgPaidOptions.some(
    bundle => bundle.pricebook.paymentType === PaymentTypeEnum.Metered,
  );
  const extraAsterisk =
    promotionalOfferExists || paidNonProfitPricebookOrEduPricebookExists ? '*' : '';

  const sections = [
    { label: 'Member pays', color: theme.palette.primary.main, options: memberPaidOptions },
    { label: 'Organization pays', color: theme.palette.alternate.main, options: orgPaidOptions },
  ].filter(s => s.options.length > 0);

  return (
    <Box display='flex' flexDirection='column' mt='20px' mb='10px'>
      <Box display='flex' flexWrap='wrap' columnGap='100px' rowGap='60px'>
        {sections.map(({ label, color, options }) => (
          <Box key={label} display='flex' flexDirection='column' gap='22px'>
            <Box display='flex' alignItems='center' columnGap='10px'>
              <AccentBar color={color} />
              <Typography variant='p16SemiBold' color='secondary.main'>
                {label}
              </Typography>
            </Box>
            <Box display='flex' flexWrap='wrap' gap='30px'>
              {options.map(bundle => (
                <PackageCard
                  key={bundle.pricebook.id}
                  pricebookBundle={bundle}
                  currentLicense={getActiveAgreement(bundle)}
                  hasActiveAgreement={hasActiveAgreement}
                  updateSignUpState={updateSignUpState}
                  signUpState={signUpState}
                  selected={_.isEqual(bundle, selectedPricebook)}
                  setSelectedPricebook={setSelectedPricebook}
                  multiplePricebooks={multiplePricebooks}
                  addingPackages={addingPackages}
                  addOnPricebook={addOnPricebookLookup.get(bundle.pricebook.id)}
                  current={
                    pricebookSelections?.some(
                      pricebook => pricebook.pricebook.id === bundle.pricebook.id,
                    ) ?? false
                  }
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>

      {(promotionalOfferExists ||
        paidNonProfitPricebookOrEduPricebookExists ||
        introPeriodExists ||
        meteredPayment) && (
        <Box display='flex' flexDirection='column' gap='10px' mt='30px'>
          {(promotionalOfferExists || paidNonProfitPricebookOrEduPricebookExists) && (
            <Typography variant='p14' color='secondary'>
              * This subscription automatically renews every month and will continue to auto-renew
              until cancelled. Terms and conditions apply. Promotional pricing discount is off
              standard monthly pricing. We reserve the right to validate eligibility and to modify
              or end this promotional offer at any time. Please see our{' '}
              <Link href={termsOfServiceDocs} target='_blank'>
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link href='/cancellation-policy' target='_blank'>
                Cancellation and Refund Policy
              </Link>{' '}
              for full details.
            </Typography>
          )}
          {introPeriodExists ? (
            <Typography variant='p14' color='secondary'>
              {extraAsterisk}* After the period of free access ends, Advice subscription auto-renews
              at $14.99/month
            </Typography>
          ) : meteredPayment ? (
            <Typography variant='p14' color='secondary'>
              {extraAsterisk}* An invoice will be sent at the end of each month for any new or
              renewing licenses.
            </Typography>
          ) : null}
        </Box>
      )}
    </Box>
  );
};

export default PackageOptions;
